import type { ColumnDef } from '@tanstack/react-table';
import EmptyCell from 'components/EmptyCell';
import { Badge } from 'components/ui/badge';
import type { TFunction } from 'i18next';
import { AlertTriangle, Ban } from 'lucide-react';
import { cn } from 'lib/utils';
import StatusCell from 'pages/AdminReferentials/components/StatusCell';
import RowActions from 'pages/AdminReferentials/components/RowActions';
import type { ReferentialTag, TechnicianDirectoryRow } from 'api/techniciansApi';
import RatingCell from './RatingCell';
import { technicianFullName, activityCount, activityDay } from '../csv';
import { AvailabilityBadge } from 'components/AvailabilityBadge';
import UnlocatedBadge from 'components/UnlocatedBadge';
import { formatDay } from 'utils/formatDate';

/** Two chips flow and wrap, then a count: "Var +1" shares a line, which keeps the row short. */
const VISIBLE = 2;

/**
 * A stable hue per referential value. The design gives each specialty its own tint and the
 * referential carries none, so it is derived from the id — same value, same pill, always.
 * Six tints, the design's own: the sixth is zinc, a neutral for what the palette ran out of.
 */
const DOTS = ['bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-violet-500', 'bg-rose-500', 'bg-zinc-500'] as const;
const PILLS = [
  'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-400',
  'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400',
  'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-400',
  'bg-violet-50 text-violet-700 dark:bg-violet-950 dark:text-violet-400',
  'bg-rose-50 text-rose-700 dark:bg-rose-950 dark:text-rose-400',
  'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300',
] as const;
const hue = (id: number): number => id % DOTS.length;

/** `+N`, a neutral pill flowing after the chips it stands for. */
const More = ({ count }: { count: number }) => (
  <span className="inline-flex h-[23px] items-center rounded-full bg-zinc-100 px-2 text-[11.5px] font-semibold text-muted-foreground dark:bg-zinc-800">
    +{count}
  </span>
);


/**
 * Specialties: a tinted pill with a dot, as the design draws them. Deactivated values show too;
 * a display never filters on `is_active`, only the pickers do.
 */
const SpecialtyPills = ({ tags }: { tags: ReferentialTag[] }) => {
  if (tags.length === 0) return <EmptyCell />;
  return (
    <div className="flex flex-wrap items-start gap-[5px]" title={tags.map((tag) => tag.name).join(', ')}>
      {tags.slice(0, VISIBLE).map((tag) => (
        <span
          key={tag.id}
          className={cn(
            'inline-flex h-[23px] max-w-full items-center gap-1.5 rounded-full pl-2 pr-[9px] text-xs font-medium',
            PILLS[hue(tag.id)]
          )}
        >
          <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', DOTS[hue(tag.id)])} aria-hidden="true" />
          <span className="truncate">{tag.name}</span>
        </span>
      ))}
      {tags.length > VISIBLE && <More count={tags.length - VISIBLE} />}
    </div>
  );
};

/** Expertises are always violet pills, areas grey squared chips read by name — the design's two shapes. */
const CHIP = {
  expertise:
    'inline-block h-[23px] max-w-full truncate rounded-full bg-violet-50 px-[9px] text-xs font-medium leading-[23px] text-violet-700 dark:bg-violet-950 dark:text-violet-400',
  area: 'inline-block h-[22px] max-w-full truncate rounded-[6px] bg-zinc-100 px-2 text-xs font-medium leading-[22px] text-foreground dark:bg-zinc-800',
} as const;

const Chips = ({ tags, kind }: { tags: ReferentialTag[]; kind: keyof typeof CHIP }) => {
  if (tags.length === 0) return <EmptyCell />;
  return (
    <div
      className="flex flex-wrap items-start gap-1"
      title={tags.map((tag) => tag.name).join(', ')}
    >
      {tags.slice(0, VISIBLE).map((tag) => (
        <span key={tag.id} className={CHIP[kind]}>
          {tag.name}
        </span>
      ))}
      {tags.length > VISIBLE && <More count={tags.length - VISIBLE} />}
    </div>
  );
};

const Text = ({ value, className }: { value: string | null; className?: string }) =>
  value ? (
    <span className={cn('block truncate text-[13px]', className)} title={value}>
      {value}
    </span>
  ) : (
    <EmptyCell />
  );

/**
 * The directory columns, all eleven drawn. Interventions and Last intervention carry the same
 * scope caveat as the record, as a header tooltip rather than repeated on every row.
 *
 * Column ids are the API's sort keys, so a header click maps straight onto `orderBy`.
 * `canManage` gates the whole Actions column: `canEdit` alone would show "Modifier" to a
 * view-only admin, since `canManageTechnicians` is keyed on `technicians:view`.
 *
 * `onOpen` and `onEdit` are two callbacks and not one: the pencil promises a modification and
 * opens the record in edit mode, while the name — the keyboard path to the row click — opens it
 * for reading, like the row itself.
 */
export const getTechniciansColumns = (
  t: TFunction,
  canManage: boolean,
  onOpen: (technician: TechnicianDirectoryRow) => void,
  onEdit: (technician: TechnicianDirectoryRow) => void
): ColumnDef<TechnicianDirectoryRow>[] => {
  // Same key as the record's muted line under the activity section — one key, two readers.
  const scopeNote = t('Counted since the assignment circuit went live.');
  const scopedHeader = (label: string) => () => (
    <span title={scopeNote}>
      {label}
      <span className="sr-only"> {scopeNote}</span>
    </span>
  );
  const columns: ColumnDef<TechnicianDirectoryRow>[] = [
    // Every sortable column carries an `accessorFn`: react-table's `getCanSort()` requires
    // one, and without it the header renders inert however `serverSorting` is wired.
    {
      id: 'name',
      header: t('Technician'),
      meta: { width: 156 },
      accessorFn: (technician) => technicianFullName(technician),
      cell: ({ row }) => {
        const technician = row.original;
        const name = technicianFullName(technician);
        return (
          <div className="min-w-0">
            {/* The row opens the record on a click; this button is the same thing at the keyboard,
                so it opens it the same way — for reading, never in edit mode. */}
            <button
              type="button"
              onClick={() => onOpen(technician)}
              className="block w-full truncate text-left text-[13.5px] font-medium leading-[1.3] hover:underline"
              title={name}
            >
              {name}
            </button>
            <div className="flex items-center gap-1.5">
              {/* The mock-up's `TECH-XXXX` exists nowhere in the repo; the email is the real datum */}
              <span className="truncate text-[11.5px] leading-[1.3] text-muted-foreground/70" title={technician.email}>
                {technician.email}
              </span>
              {!technician.externalId && (
                <span
                  className="inline-flex shrink-0 items-center text-amber-600 dark:text-amber-400"
                  title={t('No Jira account linked yet, so this technician cannot be sent an intervention request.')}
                >
                  <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                  <span className="sr-only">{t('Mobile account not provisioned')}</span>
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      id: 'phone',
      header: t('Phone'),
      enableSorting: false,
      // A 10-digit national number is 82 px; the 22 px beyond it went to the activity headers.
      meta: { width: 102 },
      cell: ({ row }) =>
        row.original.profile.phone ? (
          // nowrap + truncate: a wrapped number made every row two lines tall, an unclipped one ran into the next cell.
          <span
            className="block truncate font-mono text-xs tracking-[-0.01em] text-muted-foreground"
            title={row.original.profile.phone}
          >
            {row.original.profile.phone}
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
    },
    {
      id: 'company',
      header: t('Company'),
      meta: { width: 120 },
      accessorFn: (technician) => technician.profile.company ?? '',
      cell: ({ row }) => <Text value={row.original.profile.company} />,
    },
    {
      id: 'specialties',
      header: t('Specialties'),
      enableSorting: false,
      meta: { width: 108 },
      cell: ({ row }) => <SpecialtyPills tags={row.original.specialties} />,
    },
    {
      id: 'expertises',
      header: t('Expertises'),
      enableSorting: false,
      // 16 px moved to Actions so its header stays on one line; the design's Expertises is narrower still.
      meta: { width: 110 },
      cell: ({ row }) => <Chips tags={row.original.expertises} kind="expertise" />,
    },
    {
      id: 'country',
      header: t('Country'),
      enableSorting: false,
      meta: { width: 84 },
      // The name, as the design has it — the truncating chips are what bought the room back.
      cell: ({ row }) => <Text value={row.original.profile.countryName} className="text-muted-foreground" />,
    },
    {
      id: 'area',
      header: t('Areas covered'),
      meta: { width: 112 },
      accessorFn: (technician) => technician.geoAreas.map((area) => area.code).join(', '),
      cell: ({ row }) => <Chips tags={row.original.geoAreas} kind="area" />,
    },
    {
      id: 'rating',
      // "Note" alone: the design's header is one word and "Note moyenne" wrapped onto two lines.
      header: t('Rating'),
      // 96: "32 évaluations" at 11 px needs ~75 px plus the 18 px of cell padding.
      meta: { width: 96 },
      accessorFn: (technician) => technician.ratings.avg ?? -1,
      cell: ({ row }) => <RatingCell average={row.original.ratings.avg} count={row.original.ratings.count} />,
    },
    {
      id: 'interventions',
      header: scopedHeader(t('Interventions')),
      // The sort button is 98 px, one unbreakable word plus its chevron, and 18 px of padding.
      meta: { width: 116 },
      accessorFn: (technician) => activityCount(technician.activity) ?? -1,
      cell: ({ row }) => {
        const count = activityCount(row.original.activity);
        return <Text value={count === null ? null : String(count)} className="tabular-nums" />;
      },
    },
    {
      id: 'lastIntervention',
      header: scopedHeader(t('Last intervention')),
      // Wrapped on two lines, the button still needs 91 px for "intervention" and its chevron.
      meta: { width: 110 },
      accessorFn: (technician) => activityDay(technician.activity) ?? '',
      cell: ({ row }) => {
        const day = activityDay(row.original.activity);
        return <Text value={day && formatDay(day)} className="tabular-nums" />;
      },
    },
    // Not sortable, and never will be — a computed word rather than a stored column, and the ops
    // filter on it instead. Deliberately beside Status and not merged with it: an unavailable
    // technician keeps an active account (B-03). And no tooltip naming the period: the row does
    // not carry the periods, the record does, and shipping them for a hover would be payload the
    // table never renders.
    {
      id: 'availability',
      header: t('Availability'),
      enableSorting: false,
      // "Indispo. à venir" is the longest of the three words the badge can carry.
      meta: { width: 116 },
      cell: ({ row }) => (
        <AvailabilityBadge state={row.original.availability} className="text-[11.5px]" />
      ),
    },
    // The mock-up filters on a status it never displays; this column closes that
    {
      id: 'status',
      header: t('Status'),
      enableSorting: false,
      meta: { width: 96 },
      // Excluded is its own badge beside the account status (M13): both can be true at once.
      cell: ({ row }) => (
        <div className="flex flex-wrap gap-1">
          <StatusCell active={row.original.isActive} />
          {row.original.excludedAt && (
            <Badge variant="outline" className="gap-1 rounded-full border-rose-200 bg-rose-50 px-2 font-medium text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/40 dark:text-rose-300">
              <Ban className="h-3 w-3" aria-hidden="true" />
              {t('Banned')}
            </Badge>
          )}
          {row.original.unlocated && <UnlocatedBadge label={t('Address not located')} />}
        </div>
      ),
    },
  ];

  if (!canManage) return columns;

  return [
    ...columns,
    {
      id: 'actions',
      header: t('Actions'),
      enableSorting: false,
      // Ten columns do not fit the shell's content column, and the design's own table scrolls
      // below ~1100 px — so the scroll stays and the one interactive column is pinned to the
      // right instead of hiding behind it.
      meta: {
        width: 72,
        cellClassName:
          'sticky right-0 z-10 bg-background text-right group-hover/row:bg-muted/50',
      },
      // No lifecycle handler: activation lives on the record (L2a), so RowActions renders edit alone.
      cell: ({ row }) =>
        row.original.canEdit ? (
          <RowActions onEdit={() => onEdit(row.original)} isActive={row.original.isActive} />
        ) : null,
    },
  ];
};
